import React, { useState } from 'react';
import { Footer, Loading, TitleBar } from '../../../../../components';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { TableColumnsType } from 'antd';
import { tableSortingMethods } from '../../../../../utils';

import { QualitativeFactor, SelectedITApps } from './types';
import { GET_QUALITATIVE_FACTOR } from '../../../../dataInputs/data-inputs/qualitative-factors/queries';
import {
  GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS,
  SET_QUALITATIVE_FACTOR_MAPPING_COMMENT,
} from './queries';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import { CollapsibleText } from '../../../../../components/generic/TextTruncation';

type ITApplicationDescription = {
  uuid: string;
  application: string;
  applicationType: string;
  comment: string;
  description: string;
};

type QualitativeFactorData = {
  getQualitativeFactor: QualitativeFactor;
};

const QualitativeFactorsDescription = () => {
  const { qualitativeFactorId } = useParams();

  const [qualitativeFactorName, setQualitativeFactorName] =
    useState<string>('');

  const { loading, error } = useQuery<QualitativeFactorData>(
    GET_QUALITATIVE_FACTOR,
    {
      variables: {
        uuid: qualitativeFactorId,
      },
      onCompleted: (data) =>
        setQualitativeFactorName(data.getQualitativeFactor.name),
    },
  );

  const {
    data: qfselectedITApps,
    loading: qfSelectedITAppsLoading,
    error: qfError,
  } = useQuery<SelectedITApps>(GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS, {
    variables: {
      data: qualitativeFactorId,
    },
    skip: !qualitativeFactorId,
  });

  const [updateITApplicationComment] = useMutation(
    SET_QUALITATIVE_FACTOR_MAPPING_COMMENT,
    {
      refetchQueries: ['GetQualitativeFactorsSelectedITApplications'],
    },
  );

  const ITAppsData =
    qfselectedITApps?.getQualitativeFactorsSelectedITApplications?.[0]
      ?.itApplications ?? [];

  const columns: TableColumnsType<ITApplicationDescription> = [
    {
      title: 'Applications',
      width: 300,
      dataIndex: 'application',
      key: 'application',
      fixed: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.application, b.application),
      render: (text: string) => (
        <span className="custom-table--column ml-10">{text}</span>
      ),
    },

    {
      title: 'Description',
      dataIndex: 'mappingComment',
      key: 'mappingComment',
      fixed: 'left',
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const formFields = [
    {
      key: 'mappingComment',
      label: 'Description',
      type: 'textArea',
    },
  ];

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    return {
      qualitativeFactorUuid: qualitativeFactorId,
      comment: values?.mappingComment,
    };
  };

  let headerContent;

  if (loading) {
    headerContent = <Loading />;
  } else {
    headerContent = (
      <TitleBar
        title="Enterprise Qualitative Factor Tagging"
        subtitle={`${qualitativeFactorName} Application Descriptions`}
        brief="In the table below, describe how each application supports the Reputation factor for the company and what the impact would be on the organization if the application stopped working."
      />
    );
  }

  return (
    <>
      <TitleUpdater title={`${qualitativeFactorName}/IT App Description`} />
      <QueryErrorBoundary error={error}>{headerContent}</QueryErrorBoundary>
      <QueryErrorBoundary error={qfError}>
        <TableV3
          columns={columns}
          data={ITAppsData}
          customStyles="nested-columns--custom"
          useBorderedSection
          updateGQL={updateITApplicationComment}
          loading={qfSelectedITAppsLoading}
          tableSize="small"
          editable
          editableOptions={{
            update: true,
          }}
          inlineEditing
          formFields={formFields}
          modifyVariables={modifyVariables}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/cyber/enterprise-level/qualitative-factors/${qualitativeFactorId}/it-application-selection`}
        nextPath="/cyber/enterprise-level/qualitative-factors"
        goNextText="Overview"
      />
    </>
  );
};

export default QualitativeFactorsDescription;
